import {
  Component,
  OnInit,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl } from '@angular/forms';
import { ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

import { DirectBookingSitesService, WebsiteConfig } from 'src/shared/services/direct-booking-sites.service';
import { InquiriesService } from 'src/shared/services/inquiries.service';
import { ListingOptimizationSettingsService } from 'src/shared/services/listing-optimization-settings.service';
import { Inquiry } from 'src/models/inquiry.model';
import { environment } from 'src/environments/environment';

import { WebsiteCustomDomainComponent } from '../website-custom-domain/website-custom-domain.component';
import { WebsiteBookingLinkComponent } from '../website-booking-link/website-booking-link.component';

import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';
import { ReservationPage } from 'src/pages/reservation/reservation';
import { DIRECT_BOOKING_STATUS_ITEMS } from 'src/pages/property-listings/property-listings.status';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

type BookingLinkChip = 'Active' | 'Booked' | 'Expired' | 'Revoked';

interface BookingLinkRow {
  link: Inquiry;
  chip: BookingLinkChip;
  dotColor: string;
  amountCents: number;
}

// Long descriptions are cut to a glance; the full text lives on the Listings page.
const DESCRIPTION_PREVIEW_LENGTH = 160;

// Site detail: the Listed/Paused dropdown (same two states as the Listings
// page's direct-booking row), the site URL and a way to view it while it's
// live, a glance at the listing content with a way into the Listings page to
// edit it, custom-domain management, and the property's booking links.
@Component({
  selector: 'website-domain-settings',
  templateUrl: './website-domain-settings.component.html',
  standalone: true,
  imports: [CommonModule, TidyUiComponentsModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class WebsiteDomainSettingsComponent implements OnInit {
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly directBookingSites = inject(DirectBookingSitesService);
  private readonly inquiries = inject(InquiriesService);
  private readonly modalController = inject(ModalController);
  private readonly listingSettings = inject(ListingOptimizationSettingsService);
  private readonly fb = inject(FormBuilder);

  readonly windowService = inject(WindowService);
  readonly statusItems = DIRECT_BOOKING_STATUS_ITEMS;

  config: WebsiteConfig | null = null;
  isRightSideContent = false;
  statusControl: FormControl<string> = this.fb.nonNullable.control('paused');
  isSavingStatus = false;
  listingTitle = '';
  listingDescription = '';
  isLoadingSummary = true;
  linkRows: BookingLinkRow[] = [];
  isLoadingLinks = false;

  get statusHint(): string {
    return this.config?.is_published
      ? 'Guests can see and book this property on your direct booking site.'
      : "This property's direct booking site is off. Set it to Listed to publish it.";
  }

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.config = this.navCtrl.getParam('websiteConfig', false);
    if (!this.config) {
      const dialogParams = await this.storage.retrieve('dialog-params');
      this.config = dialogParams?.websiteConfig;
    }
    this.rightSidePanelService.setDialogPageTitle('Direct Booking Site');
    this.rightSidePanelService.setDialogPageSubtitle(
      this.config?.property_address || this.config?.property_name || ''
    );
    this.syncStatusControl();
    this.cdr.markForCheck();
    void this.refreshStatus();
    void this.loadSummary();
    void this.loadLinks();
  }

  // Re-entering the page is the reload signal now that afterPanelClosed is gone.
  ionViewWillEnter(): void {
    void this.refreshStatus();
    void this.loadSummary();
    void this.loadLinks();
  }

  // The stored config can be stale — e.g. coming back from the Listings page
  // after turning the site on — so re-read the status the panel shows.
  private async refreshStatus(): Promise<void> {
    if (!this.config) {
      return;
    }
    try {
      this.config = await this.directBookingSites.refreshConfig(this.config);
      void this.storage.save('dialog-params', { websiteConfig: this.config });
      this.syncStatusControl();
      this.cdr.markForCheck();
    } catch {
      // Keep showing the stored config.
    }
  }

  // tidy-select only re-reads its value when handed a new control, so a status
  // that changed underneath it (a refresh, a reverted pick) swaps the control.
  private syncStatusControl(): void {
    const value = this.config?.is_published ? 'listed' : 'paused';
    if (this.statusControl.value !== value) {
      this.statusControl = this.fb.nonNullable.control(value);
    }
  }

  onStatusSelect = async (next: string) => {
    if (!this.config || this.isSavingStatus) {
      return;
    }
    const listed = next === 'listed';
    if (listed === this.config.is_published) {
      return;
    }
    this.isSavingStatus = true;
    this.cdr.markForCheck();
    try {
      await this.directBookingSites.setListed(this.config.id, listed);
      this.config = { ...this.config, is_published: listed };
      void this.storage.save('dialog-params', { websiteConfig: this.config });
    } catch {
      this.util.showError('Could not update direct booking. Please try again.');
    }
    this.isSavingStatus = false;
    this.syncStatusControl();
    this.cdr.markForCheck();
  };

  private async loadSummary(): Promise<void> {
    if (!this.config) {
      return;
    }
    try {
      const { title, description } = await this.listingSettings.getListingSummary(this.config.id);
      this.listingTitle = title.trim();
      const trimmed = description.trim();
      this.listingDescription = trimmed.length > DESCRIPTION_PREVIEW_LENGTH
        ? `${trimmed.slice(0, DESCRIPTION_PREVIEW_LENGTH).trimEnd()}…`
        : trimmed;
    } catch {
      // The preview is a glance; Edit Details still opens the real content.
    }
    this.isLoadingSummary = false;
    this.cdr.markForCheck();
  }

  private async loadLinks(): Promise<void> {
    if (!this.config) { return; }
    this.isLoadingLinks = true;
    this.cdr.markForCheck();
    try {
      const links = await firstValueFrom(
        this.inquiries.getInquiries({ createdBy: 'customer', addressId: this.config.id })
      );
      this.linkRows = links.map(link => this.toLinkRow(link));
    } catch {
      this.linkRows = [];
    }
    this.isLoadingLinks = false;
    this.cdr.markForCheck();
  }

  private toLinkRow(link: Inquiry): BookingLinkRow {
    const chip = this.chipFor(link);
    return {
      link,
      chip,
      dotColor: chip === 'Active' || chip === 'Booked' ? 'var(--primary-button, #4295a3)' : '#8E8E93',
      amountCents: link.pricing?.subtotal_cents ?? link.total_amount_cents ?? 0,
    };
  }

  copySiteUrl(): void {
    if (this.config?.site_url) {
      this.util.copyToClipBoard(this.config.site_url);
    }
  }

  copyCustomDomain(): void {
    if (this.config?.custom_domain) {
      this.util.copyToClipBoard(this.config.custom_domain);
    }
  }

  goToCustomDomain = () => {
    if (!this.config) {
      return;
    }
    const params = { websiteConfig: this.config };
    void this.storage.save('dialog-params', params);
    if (this.isRightSideContent) {
      this.rightSidePanelService.navigateTo('website-custom-domain', params, WebsiteCustomDomainComponent);
    } else {
      this.navCtrl.navigateForward('website-custom-domain', params);
    }
  };

  // The Listings page owns the direct booking row for every property, including
  // ones with no connected channel listing, which have no other way to reach it.
  // Route-registry navigation, no component import (property-listings.page
  // pulls in the marketing components, so importing it back would be a cycle).
  goToListings = () => {
    if (!this.config) {
      return;
    }
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageTitle('Listings');
      this.rightSidePanelService.navigateTo('property-listings', { addressId: this.config.id });
    } else {
      localStorage.setItem('addressId', this.config.id.toString());
      this.navCtrl.navigateForward('property-listings');
    }
  };

  // Opens the live site in a new tab — the custom domain when one is set
  // (stored bare, e.g. "book.example.com"), otherwise the default site URL.
  openView = () => {
    if (this.config) {
      this.util.openUrl(this.liveSiteUrl(this.config));
    }
  };

  private liveSiteUrl(config: WebsiteConfig): string {
    return config.custom_domain ? `https://${config.custom_domain}` : config.site_url;
  }

  goToCreateBookingLink = () => {
    if (!this.config) { return; }
    const params = { websiteConfig: this.config };
    void this.storage.save('dialog-params', params);
    if (this.isRightSideContent) {
      this.rightSidePanelService.navigateTo('website-booking-link', params, WebsiteBookingLinkComponent);
    } else {
      this.navCtrl.navigateForward('website-booking-link', params);
    }
  };

  private chipFor(link: Inquiry): BookingLinkChip {
    if (link.status === 'approved') { return 'Booked'; }
    if (link.status === 'declined') { return 'Revoked'; }
    if (link.status === 'preapproved' && link.expires_at && new Date(link.expires_at) > new Date()) { return 'Active'; }
    return 'Expired';
  }

  private linkFor(link: Inquiry): string {
    const base = this.config?.custom_domain ? `https://${this.config.custom_domain}` : environment.pmsites_url;
    return `${base}/p/${link.uuid}`;
  }

  copyLink(link: Inquiry): void {
    this.util.copyToClipBoard(this.linkFor(link));
  }

  revoke = async (link: Inquiry) => {
    const params: ConfirmPageParamsModel = {
      title: 'Revoke booking link?',
      body: 'The guest will no longer be able to book from this link.',
      backText: 'Go Back',
      confirmText: 'Revoke',
      confirmAction: async () => {
        await firstValueFrom(this.inquiries.declineInquiry(link.id));
        await this.loadLinks();
        await this.modalController.dismiss();
      },
    };
    const modal = await this.modalController.create({ component: ConfirmPage, componentProps: params, animated: false, cssClass: 'confirm-modal' });
    await modal.present();
  };

  openReservation(link: Inquiry): void {
    if (!link.guest_reservation_id) { return; }
    const params = {
      guestReservation: {
        id: link.guest_reservation_id,
        check_in_date: link.check_in_date,
        check_out_date: link.check_out_date,
        guest_name: link.guest?.name,
      },
      isInquiry: false,
      address: link.property_name,
      addressId: link.address_id,
      returnTo: 'direct-booking',
    };
    // Preserve websiteConfig so the site page still has it when re-instantiated on back.
    void this.storage.save('dialog-params', { ...params, websiteConfig: this.config });
    if (this.isRightSideContent) {
      this.rightSidePanelService.navigateTo('reservation', params, ReservationPage);
    } else {
      this.navCtrl.navigateForward('reservation', params);
    }
  }

  goBack() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.goBack();
    } else {
      this.navCtrl.navigateForward('direct-booking');
    }
  }
}
